import { Component, ElementRef, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, Observable } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { CompaniesService } from 'src/app/AdvERP/companies/companies.service';
import { CompanyMasterReadByUserIdRequestDTO } from 'src/app/AdvERP/companies/companies.interface';
import { BranchMasterService } from 'src/app/Common/BranchMasterService/branchmaster.service';
import { BranchMasterReadByUserIdRequestDTO } from 'src/app/Common/BranchMasterService/branchmaster.interface';
import { GetDictionaryDTO } from 'src/app/Common/common.interface';
import { GenericListUicService } from 'src/app/Common/UICServices/ListUIC/list-uic.service';
import { CommonCloneRequestDTO } from 'src/app/Common/UICServices/ListUIC/list-uic.interface';
import { UserLoginService } from 'src/app/Common/UserLoginService/userlogin.service';

const HIDDEN_COLUMNS = new Set(['srNo', 'tenantId', 'tenantBranchId', 'createdBy', 'createdOn', 'modifiedBy', 'modifiedOn', 'isDeleted', 'isActive']);

/**
 * Generic "Clone <entity>" button for any of the 3 Common Clone tables
 * (CustomHtmlMaster, ValueListItem, PrintTemplate). Drop it into a page's
 * header the same way company-config has its own "Clone Company Config"
 * button - one button per entity, not one shared multi-tab page.
 *
 * Source is always exactly one company + branch, picked in the "Copy from"
 * dropdowns. Target can be the host page's current company/branch, or "all
 * branches" of that company - that fan-out happens server-side:
 * CommonClone_Execute takes a CloneToAllBranches flag and resolves every
 * branch of the target company itself (via dbo.BranchMaster), so this
 * component only ever needs one call for the source branch.
 *
 * Backed by AdvinMicroservices/Database/Patches/2026-09-21_CommonClone_Feature.sql
 * (CommonClone_Execute) - see that file for why it's one single SP with no
 * registry, reusing each table's own *_Create SP.
 *
 * Reading the source rows to show in the picker grid reuses each table's
 * own existing *_ReadByTenantId Angular service method (ValueListService,
 * CustomHtmlMasterService, PrintTemplateService all already have one) via
 * the [readByTenant] input, rather than a generic dispatcher SP - those
 * real, typed services already exist and are already used by the host
 * pages themselves, so there is nothing generic left to build for reads.
 *
 * Usage:
 *   <app-clone-button tableName="ValueListItem" idColumn="ValueListItemId"
 *     entityLabel="Value List Items" [targetCompanyId]="companyId" [targetBranchId]="BranchId"
 *     matchColumn="VliCode" [displayColumns]="['vlName','vliCode','vliName','vliDesc']"
 *     [readByTenant]="readValueListItemsByTenant"
 *     (cloned)="getValueListItem()"></app-clone-button>
 *
 *   // in the host component:
 *   readValueListItemsByTenant = (tenantId: number, tenantBranchId: number) =>
 *     this.valueListService.ValueListItem_readByTenantId({ tenantId, tenantBranchId })
 *       .pipe(map(res => res.items));
 */
@Component({
  selector: 'app-clone-button',
  templateUrl: './clone-button.component.html',
  styleUrls: ['./clone-button.component.css']
})
export class CloneButtonComponent {

  @Input({ required: true }) tableName!: string;
  @Input({ required: true }) idColumn!: string;
  @Input({ required: true }) entityLabel!: string;
  @Input({ required: true }) targetCompanyId!: number;
  @Input({ required: true }) targetBranchId!: number;
  /** Reads that table's rows for a given tenant/branch - pass the host page's own *_ReadByTenantId service call. */
  @Input({ required: true }) readByTenant!: (tenantId: number, tenantBranchId: number) => Observable<any[]>;

  /** Business-key column used to flag "already exists in target"; omit to skip that check. */
  @Input() matchColumn: string | null = null;
  /**
   * Extra column(s) that scope matchColumn's uniqueness, e.g. ValueListItem's
   * VliCode is only unique within a ValueListId - the same code can
   * legitimately repeat under a different ValueListId (e.g. "JobWorker"
   * under both IndividualType and CompanyType), so matching on VliCode
   * alone false-flags rows that don't actually collide in target. Mirrors
   * the natural key each table's CommonClone_Execute branch checks
   * server-side. Omit when matchColumn is already the full key (e.g.
   * TenantPrintNameConfig's DocType).
   *
   * Joined with matchColumn using U+0001 (a control character that can't
   * appear in normal form input), not '' - a plain '' join collides
   * whenever a value's own boundary is ambiguous, e.g. MasterId=1 +
   * MasterType="2page" and MasterId=12 + MasterType="page" both
   * concatenate to "12page...".
   */
  @Input() matchScopeColumns: string[] | null = null;
  /**
   * Set when this table's CommonClone_Execute branch actually skips a
   * matching row server-side (only ValueListItem, as of 2026-09-23) rather
   * than inserting a duplicate or overwriting - so selecting an
   * "exists in target" row is a no-op the admin should know about before
   * they submit, not just from the toast after the call returns.
   */
  @Input() duplicatesAreSkipped: boolean = false;
  /** Curated columns to show in the grid; omitted = every non-audit column. */
  @Input() displayColumns: string[] | null = null;
  /**
   * For a one-row-per-branch config table (e.g. CompanyDocumentNumber):
   * hides the checkbox grid (there's nothing to multi-select - it's
   * exactly one row or none), and clones that whole row instead of a
   * hand-picked id list.
   */
  @Input() singleton: boolean = false;

  @Input() buttonLabel: string = '';
  @Input() buttonClass: string = 'btn btn-sm btn-outline-primary';
  @Input() buttonIcon: string = 'bi bi-clipboard-plus';
  @Input() disabled: boolean = false;

  @Output() cloned = new EventEmitter<void>();

  @ViewChild('cloneModal', { static: true }) cloneModalContent!: ElementRef;
  private modalRef!: NgbModalRef;

  User = inject(UserLoginService).User();
  private companyService = inject(CompaniesService);
  private branchMasterService = inject(BranchMasterService);
  private gListUICService = inject(GenericListUicService);
  private modalService = inject(NgbModal);
  private toastr = inject(ToastrService);

  companyOptions: GetDictionaryDTO[] = [];
  sourceBranchOptions: GetDictionaryDTO[] = [];
  sourceCompanyIdControl = new FormControl<number | null>(null);
  sourceBranchIdControl = new FormControl<number | null>(null);

  targetBranchOptions: GetDictionaryDTO[] = [];
  targetAllBranches: boolean = false;
  isLoadingTargetBranches = false;

  rows: any[] = [];
  columns: string[] = [];
  targetMatchSet: Set<string> | null = null;
  /** One match set per target branch - used instead of targetMatchSet when targetAllBranches is on, so each row can show exactly which branch(es) already have it. */
  targetMatchSetsByBranch: Map<number, Set<string>> | null = null;
  selectedKeys = new Set<string>();
  filterText: string = '';

  isLoadingCompanies = false;
  isLoadingRows = false;
  isCloning = false;

  get label(): string {
    return this.buttonLabel || `Clone ${this.entityLabel}`;
  }

  get sourceReady(): boolean {
    return !!(this.sourceCompanyIdControl.value && this.sourceBranchIdControl.value);
  }

  /**
   * Normalized read of sourceCompanyIdControl.value. ng-select's
   * [bindValue]="'key'" sets the control's value to GetDictionaryDTO.key,
   * which is typed string - the FormControl's <number | null> generic
   * doesn't coerce that at runtime, so every comparison/DTO field built
   * from the raw control value must go through here instead of reading
   * sourceCompanyIdControl.value directly (see the loadRows() branch-match
   * bug this exact mismatch caused).
   */
  private get sourceCompanyId(): number | null {
    const v = this.sourceCompanyIdControl.value;
    return v == null ? null : Number(v);
  }

  get targetCompanyName(): string {
    return this.companyOptions.find(c => +c.key === this.targetCompanyId)?.value ?? '';
  }

  get targetBranchName(): string {
    return this.targetBranchOptions.find(b => +b.key === this.targetBranchId)?.value ?? '';
  }

  /** How many "things" are staged to clone - a row count for the grid, or 0/1 for a singleton. */
  get selectedCount(): number {
    return this.singleton ? this.rows.length : this.selectedKeys.size;
  }

  /** Of the currently selected rows, how many are flagged "exists in target" - only meaningful when duplicatesAreSkipped is set. */
  get selectedExistingCount(): number {
    if (!this.duplicatesAreSkipped || this.singleton) return 0;
    return this.rows.filter(row => this.selectedKeys.has(this.rowKey(row)) && this.rowExists(row)).length;
  }

  lowerFirst(s: string): string { return s ? s.charAt(0).toLowerCase() + s.slice(1) : s; }

  openCloneModal(): void {
    if (this.disabled) return;
    this.sourceCompanyIdControl.setValue(null);
    this.sourceBranchIdControl.setValue(null);
    this.sourceBranchOptions = [];
    this.targetAllBranches = false;
    this.rows = [];
    this.columns = [];
    this.selectedKeys.clear();
    this.filterText = '';
    this.targetMatchSet = null;
    this.targetMatchSetsByBranch = null;

    this.modalRef = this.modalService.open(this.cloneModalContent, { size: 'xl', scrollable: true });
    this.loadCompanyOptions();
    this.loadTargetBranchOptions();
  }

  private loadCompanyOptions(): void {
    this.isLoadingCompanies = true;
    const req: CompanyMasterReadByUserIdRequestDTO = { actionUser: this.User.userId.toString() };
    this.companyService.CompanyMaster_GetDictionary(req).subscribe({
      next: (res) => { this.companyOptions = res.dropDownList; this.isLoadingCompanies = false; },
      error: () => { this.isLoadingCompanies = false; }
    });
  }

  /** Loads every branch of the TARGET company - used to show the current branch's name and to drive "all branches" target mode. */
  private loadTargetBranchOptions(): void {
    this.isLoadingTargetBranches = true;
    const req: BranchMasterReadByUserIdRequestDTO = { tenantId: this.targetCompanyId, actionUser: this.User.userId.toString() };
    this.branchMasterService.BranchMaster_GetDictionary(req).subscribe({
      next: (res) => { this.targetBranchOptions = res.dropDownList; this.isLoadingTargetBranches = false; },
      error: (err) => { console.error('Failed to load target branches', err); this.isLoadingTargetBranches = false; }
    });
  }

  onSourceCompanyChange(): void {
    this.sourceBranchIdControl.setValue(null);
    this.sourceBranchOptions = [];
    this.rows = [];
    this.columns = [];
    this.selectedKeys.clear();
    const companyId = this.sourceCompanyId;
    if (!companyId) return;

    const req: BranchMasterReadByUserIdRequestDTO = { tenantId: companyId, actionUser: this.User.userId.toString() };
    this.branchMasterService.BranchMaster_GetDictionary(req).subscribe({
      next: (res) => { this.sourceBranchOptions = res.dropDownList; },
      error: (err) => console.error('Failed to load branches', err)
    });
  }

  onSourceBranchChange(): void {
    this.loadRows();
  }

  onTargetAllBranchesToggle(): void {
    this.targetMatchSet = null;
    this.targetMatchSetsByBranch = null;
    if (!this.matchColumn || !this.sourceReady) return;
    if (this.targetAllBranches) this.loadTargetMatchSetsAllBranches();
    else this.loadTargetMatchSet();
  }

  private loadRows(): void {
    this.rows = [];
    this.columns = [];
    this.selectedKeys.clear();
    this.targetMatchSet = null;
    this.targetMatchSetsByBranch = null;
    if (!this.sourceReady) return;

    const sourceCompanyId = this.sourceCompanyId!;
    // ng-select's [bindValue]="'key'" sets the control's value to
    // GetDictionaryDTO.key, which is typed string - the FormControl's own
    // <number | null> generic doesn't actually coerce it at runtime, so
    // comparing with === against a bare +b.key silently never matches.
    // Number(...) both sides so this works regardless of which type is
    // actually sitting in the control.
    const branch = this.sourceBranchOptions.find(b => Number(b.key) === Number(this.sourceBranchIdControl.value));
    if (!branch) return;

    this.isLoadingRows = true;
    this.readByTenant(sourceCompanyId, +branch.key).subscribe({
      next: (rows) => {
        this.rows = (rows || []).map(r => ({ ...r, _srcBranchId: +branch.key, _srcBranchName: branch.value }));
        this.columns = this.computeColumns(this.rows);
        this.isLoadingRows = false;
        if (this.matchColumn) {
          if (this.targetAllBranches) this.loadTargetMatchSetsAllBranches();
          else this.loadTargetMatchSet();
        }
      },
      error: (err) => {
        console.error('Failed to load rows', err);
        this.isLoadingRows = false;
        this.toastr.error('Failed to load rows.');
      }
    });
  }

  /**
   * Match key for a row: matchColumn's value, prefixed with matchScopeColumns'
   * values when given. Returns null when matchColumn is unset or its value on
   * this row is null - both callers (building a target set, checking a source
   * row) must treat null the same way (never matches), so this is the one
   * place that decides what "no key" means.
   */
  private matchKeyFor(row: any): string | null {
    if (!this.matchColumn) return null;
    const v = row[this.lowerFirst(this.matchColumn)];
    if (v == null) return null;
    const scopeParts = (this.matchScopeColumns || []).map(sc => String(row[this.lowerFirst(sc)] ?? '').toLowerCase());
    return [...scopeParts, String(v).toLowerCase()].join('');
  }

  private loadTargetMatchSet(): void {
    this.readByTenant(this.targetCompanyId, this.targetBranchId).subscribe({
      next: (rows) => {
        const set = new Set<string>();
        (rows || []).forEach(r => {
          const key = this.matchKeyFor(r);
          if (key != null) set.add(key);
        });
        this.targetMatchSet = set;
      },
      error: (err) => console.error('Failed to load target rows for match check', err)
    });
  }

  /**
   * Same "already exists" check as loadTargetMatchSet, but one set per
   * target branch instead of just the host page's current branch - used
   * when cloning into all branches so each source row can be flagged
   * against every branch it's actually about to be cloned into, not just
   * silently skip the check the way this used to.
   */
  private loadTargetMatchSetsAllBranches(): void {
    if (!this.targetBranchOptions.length) return;
    const reads = this.targetBranchOptions.map(b => this.readByTenant(this.targetCompanyId, +b.key));
    forkJoin(reads).subscribe({
      next: (results) => {
        const map = new Map<number, Set<string>>();
        results.forEach((rows, i) => {
          const set = new Set<string>();
          (rows || []).forEach(r => {
            const key = this.matchKeyFor(r);
            if (key != null) set.add(key);
          });
          map.set(+this.targetBranchOptions[i].key, set);
        });
        this.targetMatchSetsByBranch = map;
      },
      error: (err) => console.error('Failed to load target rows for match check (all branches)', err)
    });
  }

  private computeColumns(rows: any[]): string[] {
    if (this.displayColumns?.length) {
      return rows.length ? this.displayColumns.filter(c => rows[0][c] !== undefined) : this.displayColumns;
    }
    if (!rows.length) return [];
    return Object.keys(rows[0]).filter(k => !HIDDEN_COLUMNS.has(k) && !k.startsWith('_src'));
  }

  visibleRows(): any[] {
    if (!this.filterText.trim()) return this.rows;
    const needle = this.filterText.toLowerCase();
    return this.rows.filter(row =>
      this.columns.some(col => String(row[col] ?? '').toLowerCase().includes(needle))
      || String(row._srcBranchName ?? '').toLowerCase().includes(needle)
    );
  }

  rowId(row: any): string {
    return String(row[this.lowerFirst(this.idColumn)]);
  }

  /** Branch-qualified identity for the checkbox Set - two rows from different branches can share the same raw id. */
  rowKey(row: any): string {
    return `${row._srcBranchId}#${this.rowId(row)}`;
  }

  rowExists(row: any): boolean {
    const key = this.matchKeyFor(row);
    if (key == null) return false;
    if (this.targetAllBranches) {
      if (!this.targetMatchSetsByBranch) return false;
      for (const set of this.targetMatchSetsByBranch.values()) {
        if (set.has(key)) return true;
      }
      return false;
    }
    return !!this.targetMatchSet?.has(key);
  }

  /** Names of the target branches that already have a row matching this one - only meaningful when targetAllBranches is on. */
  rowExistsBranchNames(row: any): string[] {
    if (!this.targetAllBranches || !this.targetMatchSetsByBranch) return [];
    const key = this.matchKeyFor(row);
    if (key == null) return [];
    return this.targetBranchOptions
      .filter(b => this.targetMatchSetsByBranch!.get(+b.key)?.has(key))
      .map(b => b.value);
  }

  isChecked(row: any): boolean {
    return this.selectedKeys.has(this.rowKey(row));
  }

  toggleRow(row: any): void {
    const key = this.rowKey(row);
    if (this.selectedKeys.has(key)) this.selectedKeys.delete(key); else this.selectedKeys.add(key);
  }

  get isAllSelected(): boolean {
    const rows = this.visibleRows();
    return rows.length > 0 && rows.every(r => this.selectedKeys.has(this.rowKey(r)));
  }

  get isSomeSelected(): boolean {
    return this.selectedKeys.size > 0 && !this.isAllSelected;
  }

  toggleSelectAll(): void {
    const rows = this.visibleRows();
    if (this.isAllSelected) {
      rows.forEach(r => this.selectedKeys.delete(this.rowKey(r)));
    } else {
      rows.forEach(r => this.selectedKeys.add(this.rowKey(r)));
    }
  }

  /**
   * Ids of the rows to clone. Singleton tables have nothing to hand-pick -
   * empty ids there means "every active row in source scope" server side,
   * which for a singleton table is exactly that one row.
   */
  private selectedIds(): string[] {
    if (this.singleton) return [];
    return this.rows.filter(row => this.selectedKeys.has(this.rowKey(row))).map(row => this.rowId(row));
  }

  submitClone(): void {
    if (!this.selectedCount) return;

    const sourceCompanyId = this.sourceCompanyId!;
    const sourceBranchId = Number(this.sourceBranchIdControl.value);
    if (!this.targetAllBranches && sourceCompanyId === this.targetCompanyId && sourceBranchId === this.targetBranchId) {
      this.toastr.warning('Nothing to clone - source and target resolve to the same branch.');
      return;
    }

    this.isCloning = true;
    const req: CommonCloneRequestDTO = {
      tableName: this.tableName,
      sourceTenantId: sourceCompanyId,
      sourceTenantBranchId: sourceBranchId,
      targetTenantId: this.targetCompanyId,
      targetTenantBranchId: this.targetBranchId,
      cloneToAllBranches: this.targetAllBranches ? 1 : 0,
      selectedIds: this.selectedIds(),
      actionUser: this.User.userId.toString()
    };

    this.gListUICService.CommonClone(req).subscribe({
      next: (res) => {
        this.isCloning = false;
        if (res.status === 'Success') {
          this.toastr.success(res.message);
          this.modalRef.close();
          this.cloned.emit();
        } else {
          this.toastr.error(res.message || 'Clone failed.');
        }
      },
      error: (err) => {
        console.error('Clone failed', err);
        this.isCloning = false;
        this.toastr.error('Clone failed.');
      }
    });
  }
}
